import { theme, useResponsive } from "@product/scmp-sdk";
import { useMemo } from "react";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { useTakeoverBottomAdSlot } from "~/components/advertisement/takeover-ad-slot/hooks";
import { AfterPageLayout } from "~/components/after-page-layout";
import { ArticleImageGallery } from "~/components/article/article-image-gallery";
import { ImageSize } from "~/components/article/article-leading/article-leading-images/enums";
import type { ArticleRenderChildProps } from "~/components/article/article-render";
import {
  useMobileInlineAdSlotIsDisabledFlag,
  useReduceWhitespaceByAvailableSpace,
} from "~/components/article/article-render/hooks";
import {
  RightAvailableSpaceTracker,
  WidgetsBottomAvailableSpaceTracker,
} from "~/components/article/article-render/opinion-article/styles";
import { ClientSideSuspense } from "~/components/common/client-side-suspense";
import type { InlineAdSlotRenderProps } from "~/components/schema-render/common/inline-ad-slot/hooks";
import { defineInlineAdSlotSchema } from "~/components/schema-render/common/inline-ad-slot/schemas";
import { MagazinesStyleVariant as variant } from "~/components/section/section-style/const";
import { StoryCardCarouselWidget } from "~/components/section/section-style/section-style-story-card-carousel-widget";
import { useCurrentArticleIds } from "~/lib/current-item";
import type { styleArticleContent$key } from "~/queries/__generated__/styleArticleContent.graphql";

import { ArticleMoreOnThisList } from "./article-more-on-this-list";
import { StyleArticleBlockquoteQuote } from "./blockquote-quote";
import {
  ActionWidget,
  Author,
  AuthorDetail,
  Content,
  DesktopBeforeContentAdSlot,
  DesktopHeaderAdSlot,
  Follow,
  Headline,
  Leading,
  MobileUpBeforeContentAdSlot,
  MoreOnThis,
  Section,
  ShareWidget,
  Social,
  StyleArticleContainer,
  SubHeadline,
  Topic,
  TopicSocialContainer,
} from "./styles";

export type Props = {
  reference: styleArticleContent$key;
} & ArticleRenderChildProps;

export const StyleArticle: FunctionComponent<Props> = ({ provider, reference }) => {
  const article = useFragment(
    graphql`
      fragment styleArticleContent on Content {
        ...articleImageGalleryContent
        ...styleArticleAuthorBlockArticle
        ...reduceWhitespaceByAvailableSpaceArticle
        ...articleMoreOnThisListStyleArticle
        entityId
      }
    `,
    reference,
  );

  const isInlineAdSlotDisabled = useMobileInlineAdSlotIsDisabledFlag();
  const { reduceWhitespaceByAvailableSpaceSpec } = useReduceWhitespaceByAvailableSpace({
    reference: article,
    specs: {
      adSlotSize: 600,
      nativeAdSlotSize: 260,
      whitespaceAllowance: 100,
    },
    trackers: {
      right: RightAvailableSpaceTracker,
      widgetsBottom: WidgetsBottomAvailableSpaceTracker,
    },
  });

  const isDesktopUp = useResponsive(theme.breakpoints.up("desktop"), false);
  const inlineAdSlots = useMemo<InlineAdSlotRenderProps>(
    () =>
      isDesktopUp
        ? {
            "7": {
              adUnit: "native4",
              breakpoint: theme.breakpoints.up("desktop"),
            },
            style_article_1: {
              adUnit: "d_banner5",
              breakpoint: theme.breakpoints.up("desktop"),
              sizes: [[970, 250], [300, 250], [1, 1], "fluid"],
            },
            style_article_2: {
              adUnit: "d_banner2",
              breakpoint: theme.breakpoints.up("desktop"),
              sizes: [[970, 250], "fluid"],
            },
            style_article_3: {
              adUnit: "d_lrec2",
              breakpoint: theme.breakpoints.up("desktop"),
              sizes: [[300, 250], "fluid"],
            },
            style_article_4: {
              adUnit: "d_banner4",
              breakpoint: theme.breakpoints.up("desktop"),
              sizes: [[970, 250], "fluid"],
            },
            style_article_5: {
              adUnit: "d_banner4",
              breakpoint: theme.breakpoints.up("desktop"),
              sizes: [[970, 250], "fluid"],
            },
            style_article_6: {
              adUnit: "d_banner4",
              breakpoint: theme.breakpoints.up("desktop"),
              sizes: [[970, 250], "fluid"],
            },
          }
        : {
            "7": {
              adUnit: "native4",
              breakpoint: theme.breakpoints.down("desktop"),
              isDisabled: isInlineAdSlotDisabled,
            },
            style_article_1: {
              adUnit: "m_banner5",
              breakpoint: theme.breakpoints.down("desktop"),
              isDisabled: isInlineAdSlotDisabled,
              sizes: [[320, 100], [300, 250], [1, 1], "fluid"],
            },
            style_article_2: {
              adUnit: "m_lrec1",
              breakpoint: theme.breakpoints.down("desktop"),
              isDisabled: isInlineAdSlotDisabled,
              sizes: [[300, 250], [300, 600], [320, 480], "fluid"],
            },
            style_article_3: {
              adUnit: "d_lrec2",
              breakpoint: theme.breakpoints.down("desktop"),
              isDisabled: isInlineAdSlotDisabled,
              sizes: [[300, 250], [300, 600], [320, 480], "fluid"],
            },
            style_article_4: {
              adUnit: "m_lrec4d",
              breakpoint: theme.breakpoints.down("desktop"),
              isDisabled: isInlineAdSlotDisabled,
              sizes: [[300, 250], [300, 600], [320, 480], "fluid"],
            },
            style_article_5: {
              adUnit: "m_lrec4e",
              breakpoint: theme.breakpoints.down("desktop"),
              isDisabled: isInlineAdSlotDisabled,
              sizes: [[300, 250], [300, 600], [320, 480], "fluid"],
            },
            style_article_6: {
              adUnit: "m_lrec4f",
              breakpoint: theme.breakpoints.down("desktop"),
              isDisabled: isInlineAdSlotDisabled,
              sizes: [[300, 250], [300, 600], [320, 480], "fluid"],
            },
          },
    [isDesktopUp, isInlineAdSlotDisabled],
  );

  const { isEnableTakeoverBottomAdSlot } = useTakeoverBottomAdSlot({
    variant,
  });

  const { currentArticleId } = useCurrentArticleIds();
  return (
    <StyleArticleContainer>
      <DesktopHeaderAdSlot>
        {provider.adslot({
          adUnit: "d_banner1",
          autoRefreshOptions: {
            scrollBackRefreshOptions: {
              slotArticleEntityId: article.entityId,
            },
          },
          breakpoint: theme.breakpoints.up("desktop"),
          sizes: [
            [970, 250],
            [1, 1],
            [480, 360],
            [480, 270],
          ],
        })}
      </DesktopHeaderAdSlot>
      <DesktopBeforeContentAdSlot>
        {provider.adslot({
          adUnit: "d_lrec1",
          autoRefreshOptions: {
            scrollBackRefreshOptions: {
              slotArticleEntityId: article.entityId,
            },
          },
          breakpoint: theme.breakpoints.up("desktop"),
          sizes: [[300, 250], "fluid"],
          withLabel: true,
        })}
      </DesktopBeforeContentAdSlot>
      <MobileUpBeforeContentAdSlot>
        {provider.adslot({
          adUnit: "m_banner1",
          autoRefreshOptions: {
            scrollBackRefreshOptions: {
              slotArticleEntityId: article.entityId,
            },
          },
          breakpoint: theme.breakpoints.down("desktop"),
          sizes: [[300, 250], "fluid"],
          withLabel: true,
        })}
      </MobileUpBeforeContentAdSlot>
      {reduceWhitespaceByAvailableSpaceSpec.widgetsBottom.trackerNode}

      <Section>
        {provider.sections({
          variant,
        })}
      </Section>
      <Headline>{provider.headline({ variant })}</Headline>
      <Author reference={article} />
      <ArticleImageGallery reference={article}>
        <Leading>
          {provider.leading({
            advertising: {
              instream: "instream3",
            },
            responsiveDescriptionVariants: {
              desktopUp: "show",
              mobileUp: "show",
              tabletUp: "show",
            },
            responsiveImageVariants: {
              desktopUp: "normal",
              mobileUp: "square",
              tabletUp: "normal",
            },
            responsiveVariants: {
              desktopUp: ImageSize["1280x720"],
              mobileUp: ImageSize["768x768"],
              tabletUp: ImageSize["1280x720"],
            },
            withExternalGallery: true,
          })}
        </Leading>
        <Content>
          {provider.body({
            extraComponentMap: {
              "blockquote-quote": StyleArticleBlockquoteQuote,
            },
            inlineAdSlots,
            isDisabledArticleImageGallery: true,
            preprocessSchema(schema) {
              const pageCustomAdsContents = [
                defineInlineAdSlotSchema({ row: 4, tag: "style_article_1" }),
                defineInlineAdSlotSchema({ row: 8, tag: "style_article_2" }),
                defineInlineAdSlotSchema({ row: 12, tag: "style_article_3" }),
                defineInlineAdSlotSchema({ row: 16, tag: "style_article_4" }),
                defineInlineAdSlotSchema({ row: 20, tag: "style_article_5" }),
                defineInlineAdSlotSchema({ row: 24, tag: "style_article_6" }),
              ];
              let accumulateSkipCount = 0;
              let spacingCount = 0;
              const findAdSlotPosition = (
                schema: Schema,
                previousPosition: number,
                isFirst: boolean,
              ) =>
                schema.findIndex((node, index) => {
                  const previousElementRow = previousPosition - 1;
                  const lastChildren = (node?.children ?? []).slice(-1)?.[0];

                  // skip count when
                  // 1. node type is h1, h2, h3, h4, h5, track-viewed-percentage and inline-ad-slot
                  // 2. hard link
                  // 3. youtube iframe inside p or div tag
                  const isSkipCount =
                    [
                      "h1",
                      "h2",
                      "h3",
                      "h4",
                      "h5",
                      "track-viewed-percentage",
                      "inline-ad-slot",
                    ].includes(node.type) ||
                    (["p", "div"].includes(node.type) &&
                      lastChildren?.type === "iframe" &&
                      lastChildren?.subtype === ("youtube" as SchemaSubtype));

                  if (index >= previousElementRow + accumulateSkipCount || isFirst) {
                    if (isSkipCount) {
                      accumulateSkipCount++;
                    } else {
                      spacingCount++;
                    }
                  }

                  return (
                    (index >= previousElementRow + accumulateSkipCount && spacingCount === 4) ||
                    (isFirst && spacingCount === 4)
                  );
                });

              const newSchema = [...schema];
              pageCustomAdsContents.forEach((customContent, index) => {
                const previousRow = pageCustomAdsContents?.[index - 1]?.row
                  ? pageCustomAdsContents[index - 1]?.row
                  : customContent.row;
                const position = findAdSlotPosition(newSchema, previousRow, index === 0);

                if (position >= 0) {
                  newSchema.splice(position + 1, 0, customContent);
                  accumulateSkipCount++;
                }

                spacingCount = 0;
              });

              return newSchema;
            },
          })}
        </Content>
      </ArticleImageGallery>
      <TopicSocialContainer>
        <Follow>{provider.topicFollowWidget({ variant })}</Follow>
        <Social>
          <ShareWidget>{provider.shareWidget({ variant })}</ShareWidget>
          <ActionWidget>{provider.actionWidget({ variant })}</ActionWidget>
        </Social>
      </TopicSocialContainer>
      <SubHeadline>{provider.subHeadline({ variant })}</SubHeadline>
      <Topic>
        {provider.topics({
          label: "TAGS",
          variant,
          withFollow: false,
          withSeparator: false,
        })}
      </Topic>
      <AuthorDetail>{provider.authorDetails({ variant })}</AuthorDetail>
      <MoreOnThis>
        <ArticleMoreOnThisList reference={article} />
      </MoreOnThis>
      <AfterPageLayout>
        {currentArticleId === article.entityId && (
          <ClientSideSuspense>
            {isEnableTakeoverBottomAdSlot ? (
              provider.takeOverAdSlot({ variant })
            ) : (
              <StoryCardCarouselWidget />
            )}
          </ClientSideSuspense>
        )}
      </AfterPageLayout>
    </StyleArticleContainer>
  );
};

StyleArticle.displayName = "StyleArticle";
